import { supabase } from "./supabase";
import type { Rolle } from "./session";
import { velgLogo } from "@infoflyt/shared/logo";

/**
 * Konsollen: produksjonene dine.
 *
 * ========  HVORFOR DEN FINNES  ========
 *
 * Nettflata hentet ÉN produksjon — `.limit(1)` — og gikk rett inn i den.
 * Det holdt så lenge alle hadde nøyaktig én. En frilanser på tre jobber
 * så én av dem og kunne ikke komme til de andre, og den som nettopp
 * hadde opprettet en konto så «Du er ikke med i en produksjon» uten vei
 * videre. Begge deler er den samme mangelen: det fantes ikke noe sted å
 * STÅ mellom innloggingen og arbeidsflata.
 *
 * ========  VALGET HUSKES  ========
 *
 * Konsollen er landingen når ingenting er valgt. Har du gått inn i en
 * produksjon, blir du der over en omlasting — ellers ville en F5 midt i
 * en opptaksdag kastet deg ut på et oversiktsbilde. Valget ryddes når
 * man logger ut, så neste innlogging begynner på konsollen igjen.
 */

const VALGT = "infoflyt.web.produksjon";

export interface Produksjonsvalg {
  readonly medlemId: string;
  readonly produksjonId: string;
  readonly navn: string;
  readonly selskap: string | null;
  /** Fremste rolle. Har du bare en, er det den. */
  readonly rolle: Rolle;
  /** Alle rollene dine i produksjonen, uten gjentakelser. */
  readonly roller: readonly Rolle[];
  readonly sluttdatoIso: string;
  /** Ferdig valgt etter regelen i `@infoflyt/shared/logo`. */
  readonly logoSti: string | null;
  /** Laast betyr at ingen skriver i den. Se migrasjon 20261030100000. */
  readonly laast: boolean;
  /** Prodleder og produsent. De samme som setter opp. */
  readonly eier: boolean;
}

export function valgtProduksjon(): string | null {
  try { return window.localStorage.getItem(VALGT); } catch { return null; }
}

export function velgProduksjon(id: string | null): void {
  try {
    if (id === null) window.localStorage.removeItem(VALGT);
    else window.localStorage.setItem(VALGT, id);
  } catch { /* Privat vindu. Da gjelder valget bare denne fana. */ }
}

interface Rad {
  readonly id: string;
  readonly role: Rolle;
  /** Rollene de ekstra fagfunksjonene gir. Se migrasjon 20261101110000. */
  readonly extra_roles: readonly Rolle[] | null;
  readonly created_at: string;
  readonly production: {
    readonly id: string;
    readonly name: string;
    readonly end_date: string;
    readonly status: "open" | "closed";
    readonly logo_path: string | null;
    readonly company: { readonly name: string; readonly logo_path: string | null } | null;
  } | null;
}

/**
 * Rekkefolgen rollene staar i naar noen har flere enn en.
 *
 * Den fremste bestemmer ogsaa hvilken rad man gaar inn med. Oppsettet
 * foerst, fordi en som bade er prodleder og noe annet skal komme inn
 * med noekler i haanden.
 */
const FREMST: readonly Rolle[] = [
  "prodleder", "produsent", "redaktor", "afoto", "koordinator",
  "tekniker", "vj", "regi", "reporter", "klipper", "crew",
];

function fremstFoerst(a: Rolle, b: Rolle): number {
  const i = FREMST.indexOf(a), j = FREMST.indexOf(b);
  return (i < 0 ? FREMST.length : i) - (j < 0 ? FREMST.length : j);
}

/**
 * Produksjonene dine, nyeste foerst.
 *
 * ========  BARE DINE EGNE RADER  ========
 *
 * Her sto det `.order(...)` og ingenting mer, med en kommentar om at
 * policyen gjorde jobben. Det gjorde den ikke, og kunne ikke gjoere
 * den: policyen paa `production_member` svarer for ALLE medlemmene i
 * produksjonene du er med i — det er nettopp det som lar Oppsett vise
 * crewlista. Konsollen fikk dermed ett kort per MEDLEMSRAD, og en
 * produksjon med deg, en invitert prodleder og en invitert koordinator
 * sto som tre produksjoner. Brukeren 01.11.2026: «Produksjonene jeg
 * lager legger seg ogsaa opp som 3 produksjoner i konsollen.»
 *
 * `user_id` filtreres derfor her. Det er ikke en gjentakelse av en
 * regel basen alt har — basen svarer paa «hva faar du se», og dette er
 * «hva er ditt».
 *
 * ========  OG ETT KORT PER PRODUKSJON  ========
 *
 * Grunnmuren gir en person en rad per produksjon, saa i praksis er det
 * en rolle per kort. Samlingen staar likevel, og viser rollene sammen
 * om det en dag skulle bli flere — brukeren: «saa det staar at du er
 * for eksempel koordinator og prodleder, ikke en boks per rolle».
 */
export async function mineProduksjoner(): Promise<readonly Produksjonsvalg[]> {
  if (supabase === null) return [];
  const { data: oekt } = await supabase.auth.getUser();
  const minId = oekt.user?.id ?? null;
  if (minId === null) return [];

  const { data, error } = await supabase
    .from("production_member")
    .select(
      "id, role, extra_roles, created_at,"
      + " production:production(id, name, end_date, status, logo_path,"
      + " company:company(name, logo_path))",
    )
    .eq("user_id", minId)
    .order("created_at", { ascending: false });
  if (error !== null || data === null) return [];

  const samlet = new Map<string, {
    rad: Rad; p: NonNullable<Rad["production"]>; roller: Rolle[];
  }>();
  for (const rad of data as unknown as readonly Rad[]) {
    const p = rad.production;
    if (p === null) continue;
    /* Hovedrollen og de ekstra, i ett. En person er «Koordinator og
       Produksjonsleder» paa ETT kort — brukeren 01.11.2026. */
    const mine = [rad.role, ...(rad.extra_roles ?? [])];
    const f = samlet.get(p.id);
    if (f === undefined) samlet.set(p.id, { rad, p, roller: [...new Set(mine)] });
    else for (const r of mine) if (!f.roller.includes(r)) f.roller.push(r);
  }

  const ut: Produksjonsvalg[] = [];
  for (const { rad, p, roller } of samlet.values()) {
    const sortert = [...roller].sort(fremstFoerst);
    ut.push({
      medlemId: rad.id,
      produksjonId: p.id,
      navn: p.name,
      selskap: p.company?.name ?? null,
      rolle: sortert[0] ?? rad.role,
      roller: sortert,
      sluttdatoIso: p.end_date,
      logoSti: velgLogo(p.logo_path, p.company?.logo_path),
      laast: p.status === "closed",
      eier: sortert.some((r) => r === "prodleder" || r === "produsent"),
    });
  }
  return ut;
}

/** Den innloggede: id-en sletteforespoerselen henger paa, og adressen. */
export async function hvemErJeg(): Promise<{ id: string; epost: string } | null> {
  if (supabase === null) return null;
  const { data } = await supabase.auth.getUser();
  const u = data.user;
  if (u == null) return null;
  return { id: u.id, epost: u.email ?? "" };
}

/**
 * Låser produksjonen, eller låser den opp.
 *
 * Låsen er `production.status`, og den håndheves av en utløser på hver
 * tabell som bærer produksjonen — ikke av flata. Denne knappen setter
 * bare flagget; nekter basen, er det fordi du ikke setter opp
 * produksjonen. Se migrasjon 20261030100000.
 */
export async function settLaas(
  produksjonId: string, laast: boolean,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase
    .from("production")
    .update({ status: laast ? "closed" : "open" })
    .eq("id", produksjonId);
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

/**
 * Sletter produksjonen og alt som henger på den.
 *
 * ========  RADENE FØRST, SÅ FILENE  ========
 *
 * Radene går i én transaksjon i `slett_produksjon`. Filene kan ikke være
 * med der: Supabase avviser sletting rett fra `storage.objects` — «Use
 * the Storage API instead» — så de ryddes herfra etterpå.
 *
 * Rekkefølgen er ikke likegyldig. Gikk filene først og radene ikke lot
 * seg slette, sto produksjonen igjen med dokumenter som peker på
 * ingenting. Går radene først, er verste utfall noen foreldreløse filer.
 */
export async function slettProduksjon(
  produksjonId: string,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };

  const { error } = await supabase.rpc("slett_produksjon", {
    p_produksjon: produksjonId,
  });
  if (error !== null) return { ok: false, feil: error.message };

  await ryddFiler(produksjonId);
  return { ok: true };
}

/** Alt under `<produksjon>/…` i de fire bøttene, og logoen. */
async function ryddFiler(produksjonId: string): Promise<void> {
  if (supabase === null) return;
  const boetter = ["dokumenter", "plangrunnlag", "referansebilder", "rapporter"];
  for (const b of boetter) {
    try {
      const { data } = await supabase.storage.from(b).list(produksjonId);
      /* `list` gaar ett niva. Mappene under er serier og oekter, og de maa
         aapnes hver for seg — Storage har ingen rekursiv sletting. */
      for (const inn of data ?? []) {
        const { data: filer } = await supabase.storage
          .from(b).list(`${produksjonId}/${inn.name}`);
        const stier = (filer ?? []).map((f) => `${produksjonId}/${inn.name}/${f.name}`);
        if (stier.length > 0) await supabase.storage.from(b).remove(stier);
      }
      const toppnivaa = (data ?? [])
        .filter((f) => f.id !== null)
        .map((f) => `${produksjonId}/${f.name}`);
      if (toppnivaa.length > 0) await supabase.storage.from(b).remove(toppnivaa);
    } catch { /* Boetta er utilgjengelig. Radene er uansett borte. */ }
  }
  try {
    const { data } = await supabase.storage
      .from("logoer").list(`produksjon/${produksjonId}`);
    const stier = (data ?? []).map((f) => `produksjon/${produksjonId}/${f.name}`);
    if (stier.length > 0) await supabase.storage.from("logoer").remove(stier);
  } catch { /* Som over. */ }
}

/* ---- kontoen: navnet ditt og selskapet ditt --------------------------
 *
 * Begge gaar gjennom funksjoner i `public`, ikke gjennom tabellene.
 * `person.profile` ligger i et skjema som IKKE er eksponert i APIet, og
 * skal ikke bli det: invarianten sier at produksjonsskjemaet aldri
 * refererer til personskjemaet, og aa aapne hele skjemaet for aa naa ett
 * felt er aa aapne mer enn man trenger. Se migrasjon 20261030110000.
 */

export async function mittNavn(): Promise<string> {
  if (supabase === null) return "";
  const { data, error } = await supabase.rpc("mitt_navn");
  return error === null && typeof data === "string" ? data : "";
}

export async function settMittNavn(
  navn: string,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase.rpc("sett_mitt_navn", { p_navn: navn.trim() });
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

export interface MittSelskap {
  readonly id: string | null;
  readonly navn: string;
  readonly logoSti: string | null;
}

export async function mittSelskap(): Promise<MittSelskap> {
  const tom: MittSelskap = { id: null, navn: "", logoSti: null };
  if (supabase === null) return tom;
  const { data, error } = await supabase.rpc("mitt_selskap");
  if (error !== null || typeof data !== "string") return tom;

  const { data: rad } = await supabase
    .from("company").select("id, name, logo_path").eq("id", data).maybeSingle();
  if (rad === null) return tom;
  const r = rad as unknown as { id: string; name: string; logo_path: string | null };
  return { id: r.id, navn: r.name, logoSti: r.logo_path };
}

export async function settSelskap(
  navn: string,
): Promise<{ ok: boolean; feil?: string; id?: string }> {
  if (supabase === null) return { ok: true };
  const { data, error } = await supabase.rpc("sett_selskap", { p_navn: navn.trim() });
  if (error !== null) return { ok: false, feil: error.message };
  return { ok: true, id: typeof data === "string" ? data : "" };
}

/* ---- utkast ---------------------------------------------------------
 *
 * Brukeren 30.10.2026: «hvis man skulle faa opp en feilmelding, saa
 * lagres fortsatt det man har lagt inn, saa man slipper aa gjore det paa
 * nytt.»
 *
 * Utkastet ligger i nettleseren og ikke i basen, og det er med vilje: et
 * halvferdig oppsett er ikke en produksjon, og en rad i `production` som
 * bare er paabegynt ville vaert noe andre maatte forholde seg til — den
 * ville staatt i lista for alle som ser selskapet.
 *
 * LOGOEN KAN IKKE LAGRES. En `File` finnes bare saa lenge fana lever, og
 * aa lagre bildet som base64 ville lagt et par megabyte i localStorage
 * for noe man velger paa nytt paa to sekunder. Flata sier det i stedet.
 */

const UTKAST = "infoflyt.web.utkast";

export interface Utkast {
  readonly navn: string;
  readonly fra: string;
  readonly til: string;
  readonly dager: readonly string[];
  readonly rolle: "prodleder" | "produsent";
  readonly selskapsnavn: string;
  readonly mittnavn: string;
  readonly invitasjoner: readonly {
    readonly epost: string; readonly craftId: string;
    readonly navn: string; readonly fast: boolean;
  }[];
  readonly lagret: number;
}

export function hentUtkast(): Utkast | null {
  try {
    const raa = window.localStorage.getItem(UTKAST);
    if (raa === null) return null;
    const u = JSON.parse(raa) as Utkast;
    return typeof u.navn === "string" ? u : null;
  } catch { return null; }
}

export function lagreUtkast(u: Utkast): void {
  try { window.localStorage.setItem(UTKAST, JSON.stringify(u)); }
  catch { /* Privat vindu, eller fullt lager. Da er utkastet bare borte. */ }
}

export function glemUtkast(): void {
  try { window.localStorage.removeItem(UTKAST); } catch { /* som over */ }
}

/**
 * Faar du opprette en produksjon?
 *
 * ========  REGNET UT HER, HAANDHEVET I BASEN  ========
 *
 * `tilgang.kan_opprette()` er fasiten og nekter uansett — se migrasjon
 * 20261031110000. Dette er bare for aa slippe aa vise et kort som ville
 * sagt nei. En knapp som ikke virker er verre enn ingen knapp.
 *
 * Og den regnes av lista vi ALT har hentet, ikke av et nytt kall: samme
 * to tilfeller som i basen, og ingen runde til serveren for aa spoerre
 * om noe vi vet.
 *
 *   * Ingen medlemskap: dette er din foerste, og du rigger den.
 *   * Prodleder eller produsent et sted fra foer.
 *
 * En koordinator, en A-foto eller en regissoer faller utenfor begge.
 * De er lagt inn i noe andre har rigget.
 */
/* ---- fagfunksjoner man har faatt i tillegg ---------------------------
 *
 * Brukeren 01.11.2026: «Naar jeg legger til en ekstra fagfunksjon til en
 * bruker, saa maa brukeren faa en melding om det paa sine flater, slik
 * som invitasjon til en produksjon.»
 *
 * Diffen gaar mot det DENNE nettleseren sist saa, paa samme vis som
 * appen gjoer det mot det telefonen kjente. Foerste besoek melder
 * derfor ingenting: da er alt ukjent, og ukjent er ikke nytt.
 */

const SETT = "infoflyt.web.roller";

export interface NyRolle {
  readonly produksjon: string;
  readonly rolle: Rolle;
}

function lesSett(): Record<string, readonly string[]> {
  try {
    const raa = window.localStorage.getItem(SETT);
    if (raa === null) return {};
    const o = JSON.parse(raa) as Record<string, readonly string[]>;
    return typeof o === "object" && o !== null ? o : {};
  } catch { return {}; }
}

function skrivSett(o: Record<string, readonly string[]>): void {
  try { window.localStorage.setItem(SETT, JSON.stringify(o)); }
  catch { /* Privat vindu. Da gjelder beskjeden bare denne oekten. */ }
}

/**
 * Hva som er nytt siden sist, og lageret settes samtidig.
 *
 * ÉN funksjon og ikke to, fordi de to maa skje sammen: leste man uten
 * aa skrive, ville beskjeden kommet igjen ved hver omlasting.
 */
export function nyeRoller(liste: readonly Produksjonsvalg[]): readonly NyRolle[] {
  const foer = lesSett();
  const naa: Record<string, readonly string[]> = {};
  const nytt: NyRolle[] = [];
  for (const p of liste) {
    naa[p.produksjonId] = [...p.roller];
    const kjent = foer[p.produksjonId];
    /* Ukjent produksjon: enten foerste besoek, eller en invitasjon —
       og den har sin egen beskjed. */
    if (kjent === undefined) continue;
    for (const r of p.roller) {
      if (!kjent.includes(r)) nytt.push({ produksjon: p.navn, rolle: r });
    }
  }
  skrivSett(naa);
  return nytt;
}

export function kanOpprette(liste: readonly Produksjonsvalg[]): boolean {
  if (liste.length === 0) return true;
  return liste.some((p) => p.eier);
}
